import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { IAnalyticsWindow, IConfigWindow } from './dom.interface';
import { EventType } from '@nibtravel/shared/util/gtm';

/**
 * An abstraction that injects the Document global object
 * and ensure consistent implementation for angular and
 * angular universal apps.
 */
@Injectable()
export class DomService {
  constructor(@Inject(DOCUMENT) private doc: Document) {}

  /** Safely get the native Document global object */
  getDocument(): Document {
    return this.doc;
  }

  /** Safely get the native Localtion global object */
  getLocation(): Location {
    return this.doc.location;
  }

  /** Safely get the native Window global object */
  getWindow(): Window {
    return this.doc.defaultView || ({} as Window);
  }

  getUtmParams(): Record<string, string> | undefined {
    const currentUrl = this.getWindow().location?.href || '';
    const searchParams = new URL(currentUrl).searchParams;
    let params: Record<string, string> | undefined = undefined;
    searchParams.forEach((value, key) => {
      if (key.startsWith('utm_')) {
        params = { ...params, [key]: value };
      }
    });

    return params;
  }

  /** Safely get the native Window global object */
  getIConfigWindow(): IConfigWindow {
    return this.getWindow() as IConfigWindow;
  }

  /** Safely get the native Window global object */
  getAnalyticsWindow(): IAnalyticsWindow {
    const window = this.getWindow() as any;
    return Object.assign(window, {
      dataLayer: window.dataLayer || [],
    });
  }

  /** Safely get the native Window global object */
  getTeamliumAnalyticsWindow(): IAnalyticsWindow {
    const window = this.getWindow() as any;
    return Object.assign(window, {
      utag_data: window.utag_data || [],
    });
  }
  /** Safely get the GA DataLayer object */
  getDataLayer(): Record<string, unknown>[] {
    return this.getAnalyticsWindow().dataLayer;
  }

  /** Add object data into the data layer if data doesn't exist */
  addToDataLayer(data: Record<string, unknown>): void {
    const dataLayer = this.getAnalyticsWindow().dataLayer;
    if (!dataLayer.some((obj) => obj === data)) {
      this.getAnalyticsWindow().dataLayer.push(data);
    }
  }

  /** Add object data into the utag data if data doesn't exist */
  setTealiumUtagDataLayer(data: Record<string, unknown>): void {
    const utag_data = this.getTeamliumAnalyticsWindow().utag_data;
    Object.assign(utag_data, data);
  }

  pushDataToGA4(eventName: EventType, data: Record<string, unknown>) {
    const window = this.getWindow() as IAnalyticsWindow;
    if (window.gtag) {
      window.gtag('event', eventName, data);
    }
  }
  /** Method created to allow unit tests clean the data layer */
  resetDataLayer(): void {
    this.getAnalyticsWindow().dataLayer = [];
  }

  navigateTo(url: string, queryParams?: HttpParams): void {
    this.doc.location.href = queryParams
      ? `${url}?${queryParams.toString()}`
      : url;
  }
}
